import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import * as moment from 'moment-timezone';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { State } from 'reducers';
import { connect } from 'react-redux';
import { UtcTime } from 'types';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const indentedStyle = {
    marginLeft: '2em',
};

const RawStartSpecific = ({
    reportSettings,
    onChange,
    startSpecific,
    startDate,
    startTime,
    handleChangeStartSpecific,
    handleChangeStartDate,
    handleChangeStartTime,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <Fragment>
        <div>
            <label>
                <input
                    type="radio"
                    name="manual-data-start"
                    id="manual-data-start-specific"
                    checked={ startSpecific }
                    onChange={ handleChangeStartSpecific }
                />
                { t('6.1241.0.77') }
            </label>
        </div>
        <div style={ indentedStyle }>
            <Input
                id="manual-data-start-date"
                value={ startDate }
                onChange={ handleChangeStartDate }
                maxLength={ 10 }
                placeholder={ t('WC_REPORT_SCHEDULE_DATE_PLACEHOLDER') }
            />
            <Input
                id="manual-data-start-time"
                value={ startTime }
                onChange={ handleChangeStartTime }
                maxLength={ 5 }
                placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
            />
        </div>
    </Fragment>
;

type ChangeHandler = React.ChangeEventHandler<HTMLInputElement>;

type RecomposeProps = {
    startSpecific: boolean;
    startDate: string;
    startTime: string;
    handleChangeStartSpecific: ChangeHandler;
    handleChangeStartDate: ChangeHandler;
    handleChangeStartTime: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setStartDate: (s: string) => void;
    _setStartTime: (s: string) => void;
};
type StateProps = {
    _tzOffsetCalculator: (t: UtcTime) => number;
};

type DateOrTime = 'date' | 'time';
const parseStart = (reportSettings: Readonly<ReportSettings>, what: DateOrTime, tzOffsetCalculator?: (t: UtcTime) => number): string => {
    const { start } = reportSettings;
    if (start <= 0) {
        return '';
    }
    const utcStart = moment.utc(start);
    switch (what) {
        case 'date':
            // Find the start of day in server time
            utcStart.utcOffset(tzOffsetCalculator!(utcStart.valueOf())).startOf('day');
            return utcStart.format('YYYY-MM-DD');

        case 'time':
            return utcStart.clone().utcOffset(tzOffsetCalculator!(utcStart.valueOf())).format('HH:mm');
    }
};

const updateStart = ({ onChange, startDate, startTime, _tzOffsetCalculator }: OwnProps & StateProps & _RecomposeProps, force: boolean) => {
    const utcStart = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD H:mm', true); // Note: not UTC yet...
    if (utcStart.isValid()) {
        utcStart.utcOffset(-_tzOffsetCalculator(utcStart.valueOf()));
        onChange(reportSettings => reportSettings.setStart(utcStart.valueOf()));
    }
    else if (force) {
        const utcToday = moment().startOf('day');
        utcToday.utcOffset(-_tzOffsetCalculator(utcToday.valueOf()));
        onChange(reportSettings => reportSettings.setStart(utcToday.valueOf()));
    }
};

const mapStateToProps = (state: State) => ({
    _tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

type Props = OwnProps & StateProps & RecomposeProps & TranslateProps;

export const StartSpecific = compose<Props, OwnProps>(
    setDisplayName('StartSpecific'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        startSpecific: reportSettings.start > 0,
    })),
    connect<StateProps, {}, {}>(mapStateToProps),
    withState('startDate', '_setStartDate', ({ reportSettings, _tzOffsetCalculator }: OwnProps & StateProps) =>
        parseStart(reportSettings, 'date', _tzOffsetCalculator)
    ),
    withState('startTime', '_setStartTime', ({ reportSettings, _tzOffsetCalculator }: OwnProps & StateProps) =>
        parseStart(reportSettings, 'time', _tzOffsetCalculator)
    ),
    withHandlers<OwnProps & StateProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeStartSpecific: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                updateStart(props, true);
            }
        },
        handleChangeStartDate: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const startDate = e.target.value;
            props._setStartDate(startDate);
            updateStart({ ...props, startDate }, false);
        },
        handleChangeStartTime: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const startTime = e.target.value;
            props._setStartTime(startTime);
            updateStart({ ...props, startTime }, false);
        },
    }),
    lifecycle<Props & _RecomposeProps, null>({
        componentDidUpdate(oldProps: Props) {
            const { reportSettings, _setStartDate, _setStartTime, _tzOffsetCalculator } = this.props;
            if (oldProps.reportSettings.start !== reportSettings.start) {
                _setStartDate(parseStart(reportSettings, 'date', _tzOffsetCalculator));
                _setStartTime(parseStart(reportSettings, 'time', _tzOffsetCalculator));
            }
        },
    }),
)(RawStartSpecific);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/dateRange/StartSpecific.tsx